import constate from 'constate';
import { useEffect, useMemo } from 'react';
import { useQuery } from 'react-query';
import { QueryKeys } from '~/api/queryKeys';
import { Opportunities } from '~/api/scheme';
import { useSelectedRows, useUISettings } from '~/hooks/table';
import usePreviews from '~/hooks/usePreviews';
import useRightSidebar from '~/hooks/useRightSidebar';
import { useOpportunitiesSearchParams } from '~/hooks/useSearchParams';
import useSettings from '~/hooks/useSettings';
import { dirtyValues } from '~/utils/dirtyValues';
import { useFilters } from './hooks/useFilters';
import usePagingAfter from './hooks/usePagingAfter';
import useSearchForm from './hooks/useSearchForm';
import useSort from './hooks/useSort';
import { useCallbacks } from './hooks/useCallbacks';
import useListDialogs from '~/hooks/useListDialogs';

export const [ResultsProvider, useSearchResults] = constate(() => {
  const { themeStretch } = useSettings();

  const { searchParams, setSearchParams, defaultValues } = useOpportunitiesSearchParams();

  const formProps = useSearchForm();

  const sortByRelevance = useMemo(
    () => searchParams.loan_officer.name !== '',
    [searchParams.loan_officer.name]
  );

  const filtersSidebarProps = useRightSidebar();

  const paginationProps = usePagingAfter();
  const { rowsPerPage, searchAfter, resetPage, reverseSort, setNextSortId, setPrevSortId } =
    paginationProps;

  const { order, orderBy, createSortHandler, sortField } = useSort({
    orderField: sortByRelevance ? '' : 'completion_date',
    resetPage: resetPage,
  });

  const {
    data: opportunitiesResponse,
    isLoading: opportunitiesIsLoading,
    isError: opportunitiesIsError,
    refetch: opportunitiesRefetch,
    isRefetching: opportunitiesIsRefetching,
    isRefetchError: opportunitiesIsRefetchError,
  } = useQuery(QueryKeys.opportunities(), () =>
    Opportunities.SearchResults.index({
      json_params: encodeURIComponent(JSON.stringify(dirtyValues(searchParams, defaultValues))),
      paging_params: {
        limit: rowsPerPage,
        search_after: searchAfter,
        reverse_sort: reverseSort,
      },
      sort_params: { field: sortField, order },
    })
  );

  const { opportunitieSyncComplete } = useListDialogs();

  useEffect(() => {
    opportunitiesRefetch();
  }, [
    opportunitiesRefetch,
    order,
    orderBy,
    searchAfter,
    rowsPerPage,
    searchParams,
    opportunitieSyncComplete,
  ]);

  useEffect(() => {
    selectedRows.clear();
  }, [opportunitieSyncComplete]);

  const opportunitiesCount: number = Number(opportunitiesResponse?.data?.meta?.total) || 0;
  const totalLoanVolume: number = Number(opportunitiesResponse?.data?.meta?.loan_volume) || 0;
  const opportunities = opportunitiesResponse?.data.data;

  useEffect(() => {
    if (!!opportunities) {
      setNextSortId(opportunities[opportunities.length - 1]?.attributes?.search_after);
      setPrevSortId(opportunities[0]?.attributes?.search_after);
    }
  }, [opportunities, setNextSortId, setPrevSortId]);

  const UISettingsProps = useUISettings({ scope: 'opportunities_search_results' });

  const opportunitiesData = useMemo(
    () => ({
      opportunities,
      errorMessage: opportunitiesIsError || opportunitiesIsRefetchError,
      isApiReading: opportunitiesIsLoading || opportunitiesIsRefetching,
    }),
    [
      opportunities,
      opportunitiesIsError,
      opportunitiesIsLoading,
      opportunitiesIsRefetchError,
      opportunitiesIsRefetching,
    ]
  );

  const selectedRows = useSelectedRows({
    rows: opportunitiesResponse?.data.data || [],
    rowDataType: 'opportunities',
  });

  const { onSyncSuccess, onAddToListSuccess } = useCallbacks({
    clearSelectedRows: selectedRows.clear,
  });

  const { onDeleteFilter, filters } = useFilters({
    onSubmit: formProps.onSubmit,
    resetPage: resetPage,
    setValue: formProps.methods.setValue,
  });

  const { handleClick } = usePreviews();

  return {
    filters,
    filtersSidebarProps,
    formProps,
    handleClick,
    onAddToListSuccess,
    onDeleteFilter,
    onSyncSuccess,
    opportunitiesCount,
    opportunitiesData,
    paginationProps,
    searchParams,
    selectedRows,
    setSearchParams,
    sort: { order, orderBy, sortField, createSortHandler },
    themeStretch,
    totalLoanVolume,
    UISettingsProps,
  };
});
